<script lang="ts">
  import type { Run } from '../../shared/contract'
  import RunItem from './RunItem.svelte'
  import Sign from './Sign.svelte'
  import { GRADE_NAME, count, filtering, groups, matches, type Filter } from './tally'

  /**
   * An area's runs under grade headings ("BLACK · 20 OF 35 OPEN"), each group
   * groomed, then open, then closed. On a phone a filter removes the runs that
   * don't match; on the desktop board it fades them, so the board keeps its shape.
   */
  let {
    runs,
    filter,
    anyOpen,
    fade = false,
  }: { runs: readonly Run[]; filter: Filter; anyOpen: boolean; fade?: boolean } = $props()

  const f = $derived(filtering(filter))
  const shown = $derived(groups(fade || !f ? runs : runs.filter((r) => matches(r, filter))))
  const short = (grade: Run['grade'] | null) => grade ?? 'other'
  /** What a screen reader says where the heading's short word differs ("double" → "double black"). */
  const spoken = (grade: Run['grade'] | null) => (grade ? GRADE_NAME[grade] : 'ungraded')
</script>

{#each shown as group (group.grade)}
  {@const all = runs.filter((r) => (r.grade ?? null) === group.grade)}
  {@const c = count(all)}
  {@const m = all.filter((r) => matches(r, filter)).length}
  <section class="gg">
    <h4 class="mono">
      <Sign grade={group.grade} off={fade ? anyOpen && !c.open : !f && !c.open} />
      <span>
        {#if short(group.grade) === spoken(group.grade)}{short(group.grade)}{:else}<span
            aria-hidden="true">{short(group.grade)}</span
          ><span class="vh">{spoken(group.grade)}</span>{/if} ·
        {#if fade}
          {#if f}<b>{m}</b> match{:else if anyOpen}<b>{c.open}</b> of {c.total} open{:else}{c.total}
            runs{/if}
        {:else if f}
          <b>{group.runs.length}</b>{filter.status ? ` ${filter.status}` : ` of ${c.total}`}
        {:else if anyOpen}
          <b>{c.open}</b> of {c.total} open
        {:else}
          {c.total} runs
        {/if}
      </span>
    </h4>
    <ul>
      {#each group.runs as run}
        <RunItem {run} ghost={fade && f && !matches(run, filter)} />
      {/each}
    </ul>
  </section>
{:else}
  <p class="empty">{runs.length ? 'No runs here match the filter.' : 'No runs reported here.'}</p>
{/each}

<style>
  h4 {
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 30px;
    padding-top: 4px;
    color: var(--ink-3);
  }
  h4 b {
    color: var(--ink-2);
    font-weight: 400;
  }
  ul {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: minmax(26px, auto);
    column-gap: 12px;
  }
  .empty {
    color: var(--ink-3);
    padding-top: 10px;
  }
  @media (max-width: 999px) and (max-height: 760px) {
    h4 {
      height: 26px;
    }
  }
  @media (max-width: 400px) {
    ul {
      column-gap: 10px;
    }
  }
  /* Desktop focus: the board's columns do the flowing. */
  :global(.board) h4 {
    break-after: avoid;
  }
  :global(.board) ul {
    display: block;
  }
</style>
